import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, Quote, Plus } from "lucide-react";
import Section, { SectionHeader, FadeIn } from "@/components/Section";
import {
  BRAND,
  SERVICES,
  TARGET_GROUPS,
  TESTIMONIALS,
  PROCESS,
  FAQS,
  HEADLINE_MAIN,
  HEADLINE_SUB,
  COMPANIES,
  ENGAGEMENTS,
  PRESS_ARTICLE,
  PRESS_LOGOS,
} from "@/lib/brand";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/* ----- HERO ----- */
function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden pt-36 lg:pt-44 pb-24 lg:pb-32"
    >
      {/* Ambient background */}
      <div className="absolute inset-0 gradient-blue-fade" aria-hidden />
      <div className="absolute inset-0 dot-grid opacity-70" aria-hidden />
      <div className="absolute inset-0 grain" aria-hidden />

      <div className="container-edge relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          {/* LEFT: Headline */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
            >
              <div className="overline mb-6 flex items-center gap-3">
                <span className="inline-block w-8 h-px bg-white/30" />
                Coaching in der Berufswelt · Seit 2023
              </div>
              <h1
                data-testid="hero-headline"
                className="font-display text-[40px] sm:text-[56px] lg:text-[76px] xl:text-[88px] leading-[0.95] tracking-[-0.035em] text-white"
              >
                Klarheit.
                <br />
                Haltung.
                <br />
                <span className="italic font-editorial font-medium text-white/90">
                  Wirksamkeit.
                </span>
              </h1>
              <p
                data-testid="hero-subheadline"
                className="mt-8 text-white/65 text-lg lg:text-xl leading-relaxed max-w-xl"
              >
                Premium-Coaching, Interims-Geschäftsführung, Projektmandate und
                Keynotes – für Menschen und Unternehmen, die in der Berufswelt
                echte Wirkung wollen.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/kontakt" data-testid="hero-cta-primary" className="btn-primary">
                  Erstgespräch anfragen
                  <ArrowUpRight size={18} strokeWidth={1.75} />
                </Link>
                <Link to="/leistungen" data-testid="hero-cta-secondary" className="btn-ghost">
                  Leistungen ansehen <ArrowRight size={16} strokeWidth={1.75} />
                </Link>
              </div>

              <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
                {[
                  { k: "20+", v: "Jahre GF-Praxis" },
                  { k: "4", v: "GF-Mandate" },
                  { k: "850 T€", v: "EBIT-Wende Emde" },
                ].map((s) => (
                  <div key={s.v}>
                    <div className="kpi-number text-3xl lg:text-4xl text-white">
                      {s.k}
                    </div>
                    <div className="mt-2 text-[12px] uppercase tracking-[0.18em] text-white/45">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* RIGHT: Portrait treatment */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-br from-brand-500/30 via-transparent to-transparent blur-2xl opacity-70" />
              <div className="relative aspect-[3/4] rounded-[14px] overflow-hidden border border-white/10">
                <img
                  src={BRAND.photos.portrait}
                  alt={`${BRAND.name} — Coach in der Berufswelt`}
                  className="w-full h-full object-cover img-editorial"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1324] via-transparent to-transparent" />
                <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between">
                  <div>
                    <div className="text-white font-display text-xl tracking-tight">
                      {BRAND.name}
                    </div>
                    <div className="text-white/70 text-[12px] uppercase tracking-[0.22em] mt-1">
                      Coach · Interim · Mentor
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating credential card */}
              <div className="absolute -left-6 bottom-10 hidden md:block surface px-5 py-4 w-[260px] shadow-2xl">
                <div className="overline !text-[10px] !tracking-[0.22em]">
                  Aktuelle Referenz
                </div>
                <div className="mt-2 text-white text-[15px] font-medium leading-snug">
                  Interims-GF · Emde Solingen
                </div>
                <div className="text-white/55 text-[12px] mt-1 leading-snug">
                  2023–2025 · 20 Arbeitsplätze gesichert
                  <br />
                  EBIT-Wende + 850 T€ · Verkauf
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom trust band: press & speaking references */}
        <div className="mt-20 lg:mt-28 pt-8 border-t border-white/[0.06]">
          <div className="flex flex-wrap items-center justify-between gap-x-10 gap-y-6">
            <div className="overline flex items-center gap-3 whitespace-nowrap">
              <span className="inline-block w-8 h-px bg-white/30" />
              In der Presse · Als Speaker gesehen bei
            </div>
            <div className="flex flex-wrap items-center gap-x-8 lg:gap-x-10 gap-y-4">
              <span
                data-testid="press-mention-bild"
                className="inline-flex items-center h-10 px-3 rounded-md bg-white/95 border border-white/20"
                aria-label="BILD"
              >
                <img
                  src="/logos/bild.svg"
                  alt="BILD"
                  className="h-6 w-auto object-contain"
                  loading="lazy"
                />
              </span>
              <span className="w-px h-5 bg-white/15" />
              <span
                data-testid="press-mention-synaxon"
                className="inline-flex items-baseline gap-1.5 text-white/75 hover:text-white transition-colors"
                aria-label="powered by SYNAXON"
              >
                <span className="font-sans text-[12px] lg:text-[13px] tracking-[0.14em] italic text-white/50">
                  powered by
                </span>
                <span className="font-display font-extrabold text-[18px] lg:text-[20px] tracking-[0.1em]">
                  SYNAXON
                </span>
              </span>
              <span className="w-px h-5 bg-white/15" />
              <span
                data-testid="press-mention-ihk"
                className="inline-flex items-center h-10 px-3 rounded-md bg-white/95 border border-white/20"
                aria-label="Deutsche Industrie- und Handelskammer"
              >
                <img
                  src="/logos/dihk.svg"
                  alt="DIHK · Deutsche Industrie- und Handelskammer"
                  className="h-7 w-auto object-contain"
                  loading="lazy"
                />
              </span>
              <span className="w-px h-5 bg-white/15 hidden sm:block" />
              <span
                data-testid="press-mention-awo"
                className="inline-flex items-center h-10 px-3 rounded-md bg-white/95 border border-white/20"
                aria-label="AWO Service & Integration"
              >
                <img
                  src={PRESS_LOGOS.awo}
                  alt="AWO Service & Integration"
                  className="h-8 w-auto object-contain"
                  loading="lazy"
                />
              </span>
              <span className="w-px h-5 bg-white/15 hidden md:block" />
              <span
                data-testid="press-mention-aktion-mensch"
                className="inline-flex items-center h-10 px-3 rounded-md bg-white/95 border border-white/20"
                aria-label="Aktion Mensch"
              >
                <img
                  src="/logos/aktion-mensch.svg"
                  alt="Aktion Mensch"
                  className="h-6 w-auto object-contain"
                  loading="lazy"
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----- POSITIONING ----- */
function Positioning() {
  return (
    <Section id="positionierung">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <FadeIn>
            <div className="overline mb-5">Positionierung</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.02] tracking-[-0.02em] text-white">
              Coaching, das Substanz hat.
            </h2>
          </FadeIn>
        </div>
        <div className="lg:col-span-7 lg:pt-4">
          <FadeIn delay={0.1}>
            <p className="text-white/75 text-xl leading-relaxed">
              Ich arbeite mit Menschen, die in ihrer beruflichen Rolle wirklich
              etwas bewegen wollen. Keine Floskeln, keine Inszenierung – sondern
              präzise Arbeit an Haltung, Kommunikation und Entscheidungen.
            </p>
            <p className="mt-6 text-white/55 text-[17px] leading-relaxed">
              Meine Überzeugung: Echte Entwicklung entsteht im Zusammenspiel aus
              strategischer Klarheit, persönlicher Reife und dem Mut, den
              nächsten Schritt zu gehen. Ob am Anfang der Ausbildung oder
              mitten in einer Führungsrolle.
            </p>
          </FadeIn>
        </div>
      </div>
    </Section>
  );
}

/* ----- TARGET GROUPS ----- */
function TargetGroupsBlock() {
  return (
    <Section id="zielgruppen" className="bg-[#101B34]">
      <SectionHeader
        kicker="Für wen ich arbeite"
        title="Drei Karrierestufen. Eine Haltung."
        subtitle="Jede Phase der Berufswelt hat ihre eigenen Herausforderungen. Ich spreche die Sprache aller drei."
      />
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-lg overflow-hidden">
        {TARGET_GROUPS.map((g, idx) => (
          <FadeIn key={g.slug} delay={idx * 0.08}>
            <Link
              to={`/zielgruppen#${g.slug}`}
              data-testid={`target-group-${g.slug}`}
              className="group relative block p-8 lg:p-10 bg-[#0B1324] hover:bg-[#101218] transition-colors duration-500 h-full"
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-[54px] lg:text-[68px] leading-none text-white/10 group-hover:text-brand-500/40 transition-colors">
                  {g.code}
                </span>
                <ArrowUpRight
                  className="text-white/30 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
                  size={22}
                  strokeWidth={1.5}
                />
              </div>
              <div className="mt-6">
                <div className="overline">{g.kicker}</div>
                <h3 className="mt-3 font-display text-2xl lg:text-[28px] tracking-tight text-white">
                  {g.label}
                </h3>
                <p className="mt-3 text-white/55 text-[15px] leading-relaxed">
                  {g.description}
                </p>
              </div>
            </Link>
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

/* ----- SERVICES ----- */
function ServicesBlock() {
  return (
    <Section id="leistungen">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <SectionHeader
          kicker="Leistungen"
          title="Coaching-Formate mit klarem Fokus."
          subtitle="Sechs Schwerpunkte, eine Philosophie: konkrete Ergebnisse statt abstrakter Konzepte."
        />
        <Link to="/leistungen" data-testid="services-view-all" className="link-underline text-white/80 text-sm tracking-tight">
          Alle Leistungen im Detail
        </Link>
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((s, idx) => (
          <FadeIn key={s.slug} delay={idx * 0.06}>
            <Link
              to={`/leistungen#${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group relative surface block p-8 h-full transition-all duration-500 hover:border-brand-500/40 hover:-translate-y-1"
            >
              <div className="flex items-center justify-between">
                <span className="overline !tracking-[0.25em]">{s.code}</span>
                <Plus
                  size={18}
                  className="text-white/30 group-hover:rotate-45 group-hover:text-brand-400 transition-all duration-500"
                  strokeWidth={1.5}
                />
              </div>
              <h3 className="mt-8 font-display text-[22px] tracking-tight text-white leading-snug">
                {s.title}
              </h3>
              <p className="mt-3 text-white/55 text-[14.5px] leading-relaxed">
                {s.excerpt}
              </p>
              <ul className="mt-6 space-y-2">
                {s.outcomes.map((o) => (
                  <li
                    key={o}
                    className="flex items-start gap-2 text-white/65 text-[13.5px]"
                  >
                    <CheckCircle2
                      size={15}
                      className="mt-0.5 text-brand-400 flex-shrink-0"
                      strokeWidth={1.75}
                    />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </Link>
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

/* ----- WHY / CREDIBILITY ----- */
function WhyAlessandro() {
  const kpis = [
    { n: "850 T€", l: "EBIT-Wende als Interims-GF" },
    { n: "20", l: "Arbeitsplätze gesichert (Emde)" },
    { n: "20+", l: "Jahre Unternehmensführung" },
    { n: "4", l: "Gründungs- & GF-Mandate" },
  ];
  return (
    <Section id="warum" className="bg-[#101B34]">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <FadeIn>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden rounded-[12px] border border-white/10">
                <img
                  src={BRAND.photos.coaching}
                  alt="Alessandro Costantino im Coaching-Gespräch"
                  className="w-full h-full object-cover img-editorial"
                />
              </div>
              <div className="hairline mt-10" />
            </div>
          </FadeIn>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <FadeIn>
            <div className="overline mb-5">Warum Alessandro Costantino</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.02] tracking-[-0.02em] text-white">
              Coaching aus der <em className="not-italic font-editorial italic text-white/90">Praxis</em>, nicht aus dem Lehrbuch.
            </h2>
            <p className="mt-6 text-white/65 text-lg leading-relaxed">
              Ich bin Jahrgang 1977, LL.B. Bachelor of Laws, TÜV-zertifizierter
              Datenschutzbeauftragter, Personalfachkaufmann IHK – und vor allem:
              seit über 20 Jahren selbst Unternehmer, Geschäftsführer und
              Sanierer.
            </p>
            <p className="mt-5 text-white/55 text-[15.5px] leading-relaxed">
              Von Ende 2023 bis Ende 2025 habe ich die{" "}
              <span className="text-white">Emde Solingen GmbH</span> als
              Interims­geschäfts­führer vor der Insolvenz gerettet, 20
              Arbeitsplätze gesichert, das Unternehmen zukunftsfähig aufgebaut
              und erfolgreich verkauft – mit einer EBIT-Wende von{" "}
              <span className="text-white">+ 850.000 €</span>. Diese Erfahrung
              aus Krise, Entscheidung und Führung ist Teil jedes Coachings.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6">
              {kpis.map((k) => (
                <div key={k.l} className="surface p-5">
                  <div className="kpi-number text-3xl lg:text-4xl text-white">
                    {k.n}
                  </div>
                  <div className="mt-2 text-[12px] uppercase tracking-[0.18em] text-white/50 leading-snug">
                    {k.l}
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </div>
    </Section>
  );
}

/* ----- MANDATES ----- */
function MandatesBlock() {
  return (
    <Section id="mandate">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <FadeIn>
            <div className="overline mb-5">Unternehmerische Mandate</div>
            <h2 className="font-display text-4xl lg:text-[52px] leading-[1.02] tracking-[-0.02em] text-white">
              Vier Unternehmen. Eine Handschrift.
            </h2>
            <p className="mt-6 text-white/60 text-[15.5px] leading-relaxed">
              Coaching aus der Praxis heißt für mich: Ich sitze nicht nur auf
              der anderen Seite des Tisches, ich führe selbst. Auszug aktueller
              und abgeschlossener Mandate.
            </p>
          </FadeIn>
        </div>
        <div className="lg:col-span-8">
          <div className="border-t border-white/[0.08]">
            {COMPANIES.map((c, idx) => (
              <FadeIn key={c.name} delay={idx * 0.05}>
                <div
                  data-testid={`mandate-row-${idx}`}
                  className="group grid grid-cols-12 gap-4 items-start py-8 border-b border-white/[0.08]"
                >
                  <div className="col-span-12 sm:col-span-3">
                    <div className="overline !text-[10px]">{c.period}</div>
                    <div className="mt-2 text-white/80 text-[13.5px] tracking-tight">
                      {c.type}
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-7">
                    <div className="flex items-start gap-4">
                      {c.logo && (
                        <span
                          className={`inline-flex items-center justify-center h-12 min-w-[48px] max-w-[180px] rounded-md flex-shrink-0 overflow-hidden px-2 ${
                            c.logoBg === "light"
                              ? "bg-white/95 border border-white/20"
                              : "bg-white/[0.04] border border-white/10"
                          }`}
                        >
                          <img
                            src={c.logo}
                            alt={`${c.name} Logo`}
                            className="max-h-10 w-auto object-contain"
                            loading="lazy"
                          />
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-3 flex-wrap">
                          <h3 className="font-display text-2xl lg:text-[26px] tracking-tight text-white leading-snug">
                            {c.name}
                          </h3>
                          {c.status === "active" && (
                            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-brand-400 border border-brand-500/30 rounded-full px-2.5 py-0.5 mt-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-brand-400" />
                              aktiv
                            </span>
                          )}
                        </div>
                        <div className="mt-2 text-white/55 text-[14.5px]">
                          {c.role}
                        </div>
                        <p className="mt-3 text-white/50 text-[14px] leading-relaxed max-w-xl">
                          {c.description}
                        </p>
                        {c.url && (
                          <a
                            href={c.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            data-testid={`mandate-link-${idx}`}
                            className="link-underline mt-3 inline-block text-white/65 text-[13px] tracking-tight"
                          >
                            {c.url.replace(/^https?:\/\//, "")}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-2 sm:text-right">
                    <div className="kpi-number text-xl lg:text-2xl text-white">
                      {c.kpi}
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ----- ENGAGEMENTS (Interim / Projekte / Speaker / Messen) ----- */
function EngagementsBlock() {
  const speaker = ENGAGEMENTS.find((e) => e.slug === "speaker");
  const others = ENGAGEMENTS.filter((e) => e.slug !== "speaker");
  return (
    <Section id="engagements" className="relative overflow-hidden">
      <div className="absolute inset-0 gradient-blue-fade opacity-60" aria-hidden />
      <div className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <FadeIn>
              <div className="overline mb-5">Weitere Buchungs­optionen</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-[60px] leading-[1.02] tracking-[-0.02em] text-white">
                Nicht nur Coach.
                <br />
                <span className="font-editorial italic text-white/90">
                  Unternehmer, Interim-GF & Speaker.
                </span>
              </h2>
            </FadeIn>
          </div>
          <div className="lg:col-span-5">
            <FadeIn delay={0.08}>
              <p className="text-white/60 text-[16px] leading-relaxed">
                Neben dem klassischen 1:1-Coaching können Sie mich für
                Interims-Mandate, Spezialprojekte, Keynotes und Messe-Bühnen
                buchen. Ein Ansprechpartner, eine Handschrift – Executive-Niveau.
              </p>
            </FadeIn>
          </div>
        </div>

        {/* Feature row: Stage image + big first card */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <FadeIn className="lg:col-span-6">
            <Link
              to={`/kontakt?topic=${encodeURIComponent(speaker.label)}`}
              data-testid="engagement-feature-speaker"
              className="group relative block h-full rounded-[14px] overflow-hidden border border-white/10 min-h-[460px]"
            >
              <img
                src={BRAND.photos.stage}
                alt="Alessandro Costantino als Speaker auf dem IT-Sicherheitsforum"
                className="absolute inset-0 w-full h-full object-cover img-editorial"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1324] via-[#0B1324]/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8 lg:p-10">
                <div className="overline !text-[10px]">
                  Referenz · Synaxon Akademie · IT-Sicherheitsforum
                </div>
                <h3 className="mt-4 font-display text-3xl lg:text-[40px] tracking-tight text-white leading-[1.05]">
                  Speaker & Fachvorträge.
                </h3>
                <p className="mt-4 text-white/75 text-[15px] leading-relaxed max-w-lg">
                  Keynotes zu Leadership, Cybersecurity-Awareness, Compliance
                  und Karriere – klar, unterhaltsam, entscheidungs­orientiert.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-white text-[13.5px] tracking-tight">
                  Speaker anfragen
                  <ArrowUpRight
                    size={16}
                    className="group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform"
                  />
                </div>
              </div>
            </Link>
          </FadeIn>

          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {others.map((e, i) => (
              <FadeIn key={e.slug} delay={0.1 + i * 0.06}>
                <Link
                  to={`/kontakt?topic=${encodeURIComponent(e.label)}`}
                  data-testid={`engagement-card-${e.slug}`}
                  className="group surface block p-7 h-full hover:border-brand-500/40 hover:-translate-y-1 transition-all duration-500"
                >
                  <div className="flex items-center justify-between">
                    <span className="overline !tracking-[0.25em]">
                      {e.code}
                    </span>
                    <ArrowUpRight
                      size={16}
                      className="text-white/30 group-hover:text-brand-400 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3 className="mt-6 font-display text-[22px] tracking-tight text-white leading-snug">
                    {e.label}
                  </h3>
                  <p className="mt-3 text-white/55 text-[14px] leading-relaxed">
                    {e.description.split(".")[0]}.
                  </p>
                  <div className="hairline my-5" />
                  <div className="text-[11.5px] uppercase tracking-[0.18em] text-white/40">
                    {e.kicker}
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>

        {/* Detailed list below */}
        <div className="mt-16 border-t border-white/[0.08]">
          {ENGAGEMENTS.map((e, idx) => (
            <FadeIn key={`detail-${e.slug}`} delay={idx * 0.05}>
              <div
                data-testid={`engagement-detail-${e.slug}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-8 border-b border-white/[0.08]"
              >
                <div className="lg:col-span-3">
                  <div className="overline !text-[10px]">{e.kicker}</div>
                  <h3 className="mt-3 font-display text-xl lg:text-2xl text-white tracking-tight leading-snug">
                    {e.label}
                  </h3>
                </div>
                <div className="lg:col-span-6">
                  <p className="text-white/65 text-[15px] leading-relaxed">
                    {e.description}
                  </p>
                  <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
                    {e.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-white/60 text-[13.5px]"
                      >
                        <span className="mt-1.5 inline-block w-1 h-1 rounded-full bg-brand-400 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-3 flex lg:justify-end lg:items-center">
                  <Link
                    to={`/kontakt?topic=${encodeURIComponent(e.label)}`}
                    data-testid={`engagement-cta-${e.slug}`}
                    className="btn-ghost text-[13.5px] py-2.5 px-5"
                  >
                    {e.cta} <ArrowUpRight size={15} />
                  </Link>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ----- PRESS (BILD 2020) ----- */
function PressBlock() {
  return (
    <Section id="press" className="bg-[#101B34]">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <FadeIn className="lg:col-span-7">
          <Link
            to="/ueber-mich#presse"
            data-testid="home-press-article"
            className="group block relative overflow-hidden rounded-[12px] border border-white/10 bg-[#F4F5F7]"
          >
            <img
              src={PRESS_ARTICLE.image}
              alt="BILD-Artikel 2020 über Alessandro Costantino"
              className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
              loading="lazy"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/10 pointer-events-none" />
          </Link>
        </FadeIn>

        <FadeIn delay={0.08} className="lg:col-span-5">
          <div className="overline mb-5 flex items-center gap-3">
            <span className="inline-block w-6 h-px bg-white/30" />
            In der Presse · {PRESS_ARTICLE.date}
          </div>
          <h2 className="font-display text-3xl lg:text-[44px] tracking-[-0.02em] text-white leading-[1.05]">
            Und einmal – ganz aus Versehen –
            <br />
            <span className="font-editorial italic text-white/90">
              auch auf der Titelseite der BILD.
            </span>
          </h2>
          <p className="mt-6 text-white/65 text-[16px] leading-relaxed">
            Als Geschäftsführer der AWO Service & Integration GmbH habe ich
            Menschen mit Behinderungen echte Arbeit gegeben – und bedürftigen
            Familien in Solingen geholfen. Das hat die BILD 2020 porträtiert.
            Ich bin dafür bis heute dankbar.
          </p>
          <Link
            to="/ueber-mich#presse"
            data-testid="home-press-link"
            className="btn-ghost mt-8 text-[13.5px] py-2.5 px-5"
          >
            Den Artikel lesen
            <ArrowUpRight size={15} />
          </Link>
        </FadeIn>
      </div>
    </Section>
  );
}

/* ----- PROCESS ----- */
function ProcessBlock() {
  return (
    <Section id="prozess">
      <SectionHeader
        kicker="Zusammenarbeit"
        title="Ein Prozess, der trägt."
        subtitle="Struktur gibt Sicherheit. So sieht der Weg zu messbarer Wirkung aus."
      />
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PROCESS.map((p, i) => (
          <FadeIn key={p.step} delay={i * 0.07}>
            <div className="relative surface p-8 h-full">
              <div className="kpi-number text-[64px] text-white/10 leading-none">
                {p.step}
              </div>
              <h3 className="mt-4 font-display text-xl tracking-tight text-white">
                {p.title}
              </h3>
              <p className="mt-3 text-white/55 text-[14.5px] leading-relaxed">
                {p.description}
              </p>
              {i < PROCESS.length - 1 && (
                <div className="hidden lg:block absolute top-[46px] -right-3 text-white/15">
                  <ArrowRight size={18} strokeWidth={1.25} />
                </div>
              )}
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

/* ----- TESTIMONIALS ----- */
function TestimonialsBlock() {
  return (
    <Section id="stimmen" className="bg-[#101B34]">
      <SectionHeader
        kicker="Stimmen"
        title="Wie Klient:innen die Arbeit erleben."
      />
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <FadeIn key={i} delay={i * 0.08}>
            <div className="surface p-8 h-full flex flex-col">
              <Quote
                className="text-brand-500/70"
                size={26}
                strokeWidth={1.25}
              />
              <p className="mt-6 text-white/80 text-[16px] leading-relaxed font-editorial italic">
                „{t.quote}"
              </p>
              <div className="mt-auto pt-8">
                <div className="hairline mb-5" />
                <div className="text-white text-[14px] tracking-tight">
                  {t.author}
                </div>
                <div className="text-white/45 text-[12px] tracking-[0.18em] uppercase mt-1">
                  {t.role}
                </div>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
      <p className="mt-8 text-white/35 text-xs tracking-[0.18em] uppercase text-center">
        Stimmen anonymisiert auf Wunsch der Klient:innen
      </p>
    </Section>
  );
}

/* ----- FAQ ----- */
function FaqBlock() {
  return (
    <Section id="faq">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-4">
          <SectionHeader kicker="Häufige Fragen" title="Kurz und klar." />
          <p className="mt-6 text-white/55 text-[15.5px] leading-relaxed">
            Wenn Ihre Frage hier nicht beantwortet wird, schreiben Sie mir
            einfach. Ich antworte persönlich.
          </p>
        </div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-white/[0.08]"
              >
                <AccordionTrigger className="text-left text-white py-6 hover:no-underline font-display text-lg lg:text-xl tracking-tight">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/60 leading-relaxed text-[15.5px] pb-8 pr-10">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </Section>
  );
}

/* ----- CTA ----- */
function FinalCTA() {
  return (
    <Section id="cta" className="relative overflow-hidden">
      <div className="absolute inset-0 gradient-blue-fade opacity-80" aria-hidden />
      <div className="relative surface p-10 lg:p-20 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="overline mb-5">Der nächste Schritt</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1] tracking-[-0.025em] text-white">
              Sprechen wir.
              <br />
              <span className="italic font-editorial text-white/90">
                Kostenfrei. Vertraulich. Auf Augenhöhe.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/kontakt" data-testid="cta-final-button" className="btn-primary text-base px-7 py-4">
              Termin anfragen <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </Section>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Positioning />
      <TargetGroupsBlock />
      <ServicesBlock />
      <WhyAlessandro />
      <MandatesBlock />
      <EngagementsBlock />
      <PressBlock />
      <ProcessBlock />
      <TestimonialsBlock />
      <FaqBlock />
      <FinalCTA />
    </div>
  );
}
